import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { SearchInput, Badge, Button, DataTable, StatePanel } from "../../components/ui";
import { getFileUrl } from "../../core/config/api.js";
import DemandMeter from "../components/DemandMeter.jsx";
import StockStatePill from "../components/StockStatePill.jsx";
import TimeWindowPills, { TIME_WINDOW_OPTIONS } from "../components/TimeWindowPills.jsx";

// react-query-cached product fetch — dedupes across rows that share product_id.
const fetchProduct = async (productId) => {
  const { data } = await axios.get(`/api/products/${productId}`);
  return data;
};

const useProductForRow = (productId) =>
  useQuery({
    queryKey: ["product", productId],
    queryFn: () => fetchProduct(productId),
    enabled: !!productId,
    staleTime: 5 * 60_000,
  });

const InsightsThumb = ({ productId }) => {
  const { data: product } = useProductForRow(productId);
  const imageId =
    product?.image_id ?? product?.imageId ?? product?.images?.[0]?.id ?? null;
  const imageUrl = imageId ? getFileUrl(imageId) : null;

  return (
    <span className="core-list-insights__ref-thumb">
      {imageUrl ? (
        <img src={imageUrl} alt="" />
      ) : (
        <span className="core-list-insights__ref-thumb-placeholder" />
      )}
    </span>
  );
};

const refLinkFor = (row) =>
  row.product_id ? (
    <Link to={`/view-product/${row.product_id}`} className="core-list-insights__ref-link">
      {row.reference || "—"}
    </Link>
  ) : (
    <span>{row.reference || "—"}</span>
  );

const brandModelFor = (row) => [row.brand, row.model].filter(Boolean).join(" · ") || null;

const InsightsRefCell = ({ row }) => (
  <div className="core-list-insights__ref-cell">
    <InsightsThumb productId={row.product_id} />
    <div className="core-list-insights__ref-info">
      {refLinkFor(row)}
      <span className="core-list-insights__ref-meta">{brandModelFor(row)}</span>
    </div>
  </div>
);

const fetchInsights = async (storeIdM, period) => {
  const { data } = await axios.get(`/api/core-list/insights`, {
    params: { store_id_m: storeIdM, period },
  });
  return data;
};

const matchesSearch = (ref, brand, model, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (ref.reference || "").toLowerCase().includes(q) ||
    (brand || "").toLowerCase().includes(q) ||
    (model || "").toLowerCase().includes(q)
  );
};

/**
 * Fontaine M60A — the Insights result surface composes the shared `ft-table`
 * primitive: ONE ranked DataTable (the spec's "#" column implies a single
 * flattened list, not brand/model grouping), a 36px compact table at ≥900px
 * and one Card per reference below it. Empty / no-results / error all go
 * through the primitive's own `state` slot (StatePanel replaces the rows,
 * the toolbar stays put). Nothing about the data, the endpoints, the
 * session-added optimistic set or the add flow changed — see
 * `tests/stock/CoreListInsights.test.jsx`, green before and after this
 * migration.
 */
const CoreListInsights = ({ storeIdM, stockStateByProductId, onAdded }) => {
  const [windowType, setWindowType] = useState(TIME_WINDOW_OPTIONS[0].id);
  const [search, setSearch] = useState("");
  const [addedIds, setAddedIds] = useState(() => new Set());

  const {
    data,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["core-list-insights", storeIdM, windowType],
    queryFn: () => fetchInsights(storeIdM, windowType),
    enabled: storeIdM != null,
    staleTime: 0,
  });

  useEffect(() => {
    setAddedIds(new Set());
  }, [windowType, storeIdM]);

  const groups = useMemo(() => data?.groups || [], [data]);

  // Flatten to a single, numbered list — rank is the position in the
  // filtered list, exactly the `i + 1` the legacy tbody rendered.
  const rows = useMemo(() => {
    const flat = [];
    groups.forEach((brandGroup) => {
      (brandGroup.models || []).forEach((modelGroup) => {
        (modelGroup.references || []).forEach((ref) => {
          if (matchesSearch(ref, brandGroup.brand, modelGroup.model, search)) {
            flat.push({
              ...ref,
              brand: brandGroup.brand,
              model: modelGroup.model,
              rank: flat.length + 1,
            });
          }
        });
      });
    });
    return flat;
  }, [groups, search]);

  const hasAnyData = groups.length > 0;
  const isEmpty = !isFetching && !hasAnyData;
  const noResults = !isFetching && hasAnyData && rows.length === 0;

  const onAddToCoreList = async (productId) => {
    if (storeIdM == null || productId == null) return;
    try {
      await axios.post(`/api/core-list`, {
        store_id_m: storeIdM,
        product_ids: [productId],
      });
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.add(productId);
        return next;
      });
      onAdded && onAdded();
    } catch (e) {
      console.error("Failed to add to Core List", e);
      alert("Failed to add to Core List. Please try again.");
    }
  };

  if (storeIdM == null) {
    return (
      <StatePanel
        state="empty"
        icon="Store"
        title="Insights are only available for stores with data wired up."
        description={null}
      />
    );
  }

  // One verdict for the primitive's state slot; `undefined` means rows render.
  let state;
  let stateProps;
  if (isError) {
    state = "error";
    stateProps = {
      title: "Couldn't load Insights",
      description: "Please try again.",
      onRetry: () => refetch(),
    };
  } else if (isEmpty) {
    state = "empty";
    stateProps = { title: "No leads in this time window.", description: null };
  } else if (noResults) {
    state = "no-results";
    stateProps = { title: "No references match your search.", description: null };
  }

  const columns = [
    {
      key: "rank",
      header: "#",
      cardLabel: "Rank",
      className: "core-list-insights__col--rank",
      cell: (row) => row.rank,
    },
    {
      key: "reference",
      header: "Reference",
      card: "title",
      // Laptop keeps the composite cell (thumbnail, link, brand · model
      // beneath). On a card the SAME parts land in the primitive's own bands
      // via the two tableHidden columns below, so the headline stays a single
      // ellipsised line like every other Fontaine card.
      cell: (row, meta) =>
        meta.presentation !== "table" ? refLinkFor(row) : <InsightsRefCell row={row} />,
    },
    {
      key: "thumb",
      header: "Photo",
      card: "aside",
      tableHidden: true,
      cell: (row) => <InsightsThumb productId={row.product_id} />,
    },
    {
      key: "brandModel",
      header: "Brand · Model",
      card: "subtitle",
      tableHidden: true,
      emptyValue: null, // no brand/model is nothing, not an em dash under the headline
      cell: (row) => brandModelFor(row),
    },
    {
      key: "contacts",
      header: "Distinct contacts",
      card: "meta",
      cell: (row) => <DemandMeter count={row.contact_count} unit="contacts" />,
    },
    {
      key: "state",
      header: "Stock state",
      card: "meta",
      cell: (row) => <StockStatePill state={stockStateByProductId?.get(row.product_id) || null} />,
    },
    {
      key: "actions",
      header: "Action",
      align: "end",
      card: "actions",
      cell: (row) => {
        const isAlreadyInCoreList = row.already_in_core_list || addedIds.has(row.product_id);
        return isAlreadyInCoreList ? (
          <Badge tone="success">✓ In Core List</Badge>
        ) : (
          // Shared Button primitive — a bare <button> here inherits theme.css's
          // global `button { height: 49px; text-transform: uppercase }` (the
          // ST-4 bug flagged on this tab's legacy add button).
          <Button
            size="md"
            variant="secondary"
            onClick={() => onAddToCoreList(row.product_id)}
          >
            + Add to Core List
          </Button>
        );
      },
    },
  ];

  return (
    <div className="core-list-insights">
      <div className="core-list-insights__toolbar">
        <SearchInput
          size="lg"
          className="core-list-insights__search"
          placeholder="Search reference, brand or model"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch("")}
        />
        <TimeWindowPills value={windowType} onChange={setWindowType} />
      </div>

      {/* Nothing rendered while the first fetch is in flight — exactly the
          legacy behaviour (a window/store change is a new query key, so its
          in-flight render has no data and no verdict either). */}
      {(state || rows.length > 0) && (
        <DataTable
          className="core-list-insights__ft"
          caption="References ranked by buyer demand"
          columns={columns}
          rows={rows}
          rowKey="product_id"
          state={state}
          stateProps={stateProps}
        />
      )}
    </div>
  );
};

export default CoreListInsights;
